import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useWalletDeposit, useWalletDepositInfo, useProfile } from "../hooks/useApi";
import GlassModal from "./GlassModal";
import Button from "./Button";
import InlinePhoneVerification from "./InlinePhoneVerification";
import { formatNumber, parsePersianNumber, toPersianDigits, normalizeDigits } from "../utils/format";
import { isInvoiceInfoComplete } from "../utils/profile";

interface DepositModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-filled charge amount in Toman (e.g. the amount needed to complete a plan purchase). */
  presetAmount?: number | null;
}

export default function DepositModal({ open, onClose, presetAmount = null }: DepositModalProps) {
  const { data: depositInfo } = useWalletDepositInfo();
  const { data: user } = useProfile();
  const deposit = useWalletDeposit();

  const invoiceInfoComplete = isInvoiceInfoComplete(user);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isPhoneVerified = !!((user as any)?.phone) && !!((user as any)?.phone_verified);

  const [amount, setAmount] = useState("");
  const [agreed, setAgreed] = useState(false);

  const MIN_DEPOSIT_TOMAN = depositInfo ? Math.floor(depositInfo.min_deposit / 10) : 50000;
  const TAX_RATE = depositInfo ? Number(depositInfo.tax_rate) : 0.1;

  const effectivePreset = presetAmount ? Math.max(presetAmount, MIN_DEPOSIT_TOMAN) : null;

  useEffect(() => {
    if (!open) return;
    if (effectivePreset) {
      setAmount(String(effectivePreset));
      setAgreed(false);
    }
  }, [open, effectivePreset]);

  const toman = parsePersianNumber(amount);
  const amountValid = !isNaN(toman) && toman >= MIN_DEPOSIT_TOMAN;
  const showMinError = amount !== "" && !isNaN(toman) && toman < MIN_DEPOSIT_TOMAN;
  const taxToman = amountValid ? toman * TAX_RATE : 0;
  const totalToman = amountValid ? toman + taxToman : 0;

  const handleDeposit = () => {
    if (!amountValid || !agreed) return;
    // Convert Toman to IRR (Rial) — multiply by 10
    // Modal stays open with loading state until redirect to ZarinPal
    deposit.mutate(toman * 10);
  };

  // When opened via Subscription or Wallet, require phone verification before deposit
  const showPhoneGate = !!user && !isPhoneVerified;

  return (
    <GlassModal
      open={open}
      onClose={onClose}
      title={showPhoneGate ? "تأیید شماره موبایل" : "افزایش موجودی"}
    >
      {showPhoneGate ? (
        <InlinePhoneVerification
          initialPhone={(user as any)?.phone || ""}
          onVerified={() => {
            // stays open; isPhoneVerified will flip after query invalidation
            // so deposit form appears automatically
          }}
          onCancel={onClose}
        />
      ) : (
        <div className="space-y-4">
        {presetAmount != null && (
          <div className="flex items-start gap-2.5 rounded-btn bg-primary/5 p-3.5">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary shrink-0 mt-0.5">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <p className="text-small text-text-secondary">
              این مبلغ بر اساس قیمت پلن انتخابی شما پیش‌فرض شده و قابل ویرایش است.
            </p>
          </div>
        )}

        <div>
          <label className="block text-small font-medium text-text-secondary mb-1.5">
            مبلغ (تومان)
          </label>
          <div className="relative">
            <input
              type="text"
              inputMode="numeric"
              value={toPersianDigits(amount)}
              onChange={(e) => {
                const val = normalizeDigits(e.target.value);
                setAmount(val);
              }}
              className={`w-full h-12 px-4 rounded-btn border text-body text-text-main text-right placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all font-number tracking-tight ${showMinError ? "border-status-error-text" : "border-line"}`}
              placeholder="مثال: ۵۰۰۰۰"
              dir="ltr"
            />
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-body text-text-tertiary">تومان</span>
          </div>
          {showMinError && (
            <p className="text-small text-status-error-text mt-1.5">
              حداقل مبلغ قابل پرداخت {formatNumber(MIN_DEPOSIT_TOMAN)} تومان است
            </p>
          )}
          <p className="text-small text-text-tertiary mt-1.5">
            حداقل: {formatNumber(MIN_DEPOSIT_TOMAN)} تومان ({formatNumber(MIN_DEPOSIT_TOMAN * 10)} ریال)
          </p>
        </div>

        <div className="flex gap-2">
          {["۱۰۰,۰۰۰", "۵۰۰,۰۰۰", "۱,۰۰۰,۰۰۰"].map((preset) => (
            <button
              key={preset}
              onClick={() => setAmount(preset.replace(/,/g, ""))}
              className="flex-1 py-2 rounded-btn border border-line text-small text-text-secondary hover:bg-surface-hover hover:border-primary/30 transition-all"
            >
              {preset}
            </button>
          ))}
        </div>

        {amountValid && (
          <div className="bg-surface-hover rounded-btn p-4 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-small text-text-tertiary">مبلغ</span>
              <span className="text-body font-number text-text-main">{formatNumber(toman)} تومان</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-small text-text-tertiary">مالیات (۱۰٪)</span>
              <span className="text-body font-number text-text-main">{formatNumber(taxToman)} تومان</span>
            </div>
            <div className="border-t border-line" />
            <div className="flex items-center justify-between">
              <span className="text-small font-medium text-text-secondary">مبلغ قابل پرداخت</span>
              <span className="text-body font-semibold font-number text-primary">{formatNumber(totalToman)} تومان</span>
            </div>
          </div>
        )}

        <label className="flex items-start gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded accent-primary"
          />
          <span className="text-small text-text-secondary">
            با{" "}
            <a
              href="https://asha-ai.ir/terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              قوانین و شرایط استفاده
            </a>{" "}
            موافقم
          </span>
        </label>

        {!invoiceInfoComplete && (
          <div className="flex items-start gap-2.5 rounded-btn bg-status-warning-bg p-3.5">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-status-warning-text shrink-0 mt-0.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <p className="text-small text-status-warning-text">
              برای دریافت فاکتور، لطفاً ابتدا{" "}
              <Link to="/profile" className="font-medium underline hover:opacity-80">
                اطلاعات هویتی خود را در صفحه پروفایل
              </Link>{" "}
              تکمیل کنید.
            </p>
          </div>
        )}

        <Button
          className="w-full !py-3"
          onClick={handleDeposit}
          loading={deposit.isPending}
          disabled={!amountValid || !agreed}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
          پرداخت از طریق درگاه زرین‌پال
        </Button>

        <p className="text-small text-text-tertiary text-center flex items-center justify-center gap-1.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          پرداخت امن توسط درگاه زرین‌پال
        </p>
      </div>
      )}
    </GlassModal>
  );
}
